import {
  departmentDefaultTarget,
  indexTargets,
  lookupEditorTarget,
} from "@/grid/data/resolve-target";
import {
  RECURRING_WEEK_PERIOD_START,
  RECURRING_WEEK_PERIOD_TYPE,
} from "@/grid/core/target-periods";
import {
  metricsForStorage,
  metricsNeedingExplicitMonth,
  metricsNeedingExplicitQuarter,
  type TargetMetricConfig,
} from "@/grid/core/target-policy";
import { allMonthStartsInCalendarQuarter } from "@/grid/core/periods";
import type {
  DepartmentDefinition,
  MetricDefinition,
  TargetPeriodType,
  TargetRow,
} from "@/grid/core/types";
import type { TeamGroup } from "@/grid/data/assemble-grid";

export interface TargetSheetSlice {
  defaults: Record<string, number | null>;
  teams: Record<string, Record<string, number | null>>;
}

export interface WeekTargetSheetValues {
  recurring: TargetSheetSlice;
  overrides: TargetSheetSlice;
}

export interface MonthlyTargetSheet {
  monthStart: string;
  sheet: TargetSheetSlice;
}

export interface TargetEditorSheets {
  weekly: WeekTargetSheetValues | null;
  /** One sheet per calendar month in the anchor quarter (for QTD roll-up). */
  monthlyQuarters: MonthlyTargetSheet[];
  quarterly: TargetSheetSlice | null;
}

function buildPeriodTargetSheetValues(
  department: DepartmentDefinition,
  teams: TeamGroup[],
  targets: TargetRow[],
  periodType: TargetPeriodType,
  periodStart: string,
  metrics: MetricDefinition[],
): TargetSheetSlice {
  const index = indexTargets(targets);
  const defaults: Record<string, number | null> = {};
  const byTeam: Record<string, Record<string, number | null>> = {};

  for (const metric of metrics) {
    defaults[metric.id] = departmentDefaultTarget(
      index,
      department.id,
      metric.targetMetric,
      periodType,
      periodStart,
    );
  }

  for (const team of teams) {
    byTeam[team.groupKey] = {};
    for (const metric of metrics) {
      byTeam[team.groupKey][metric.id] = lookupEditorTarget(
        index,
        team.groupKey,
        metric.targetMetric,
        periodType,
        periodStart,
      );
    }
  }

  return { defaults, teams: byTeam };
}

function mergeMonthlyMetrics(
  department: DepartmentDefinition,
  configByMetric: Map<string, TargetMetricConfig>,
): MetricDefinition[] {
  const seen = new Set<string>();
  const result: MetricDefinition[] = [];

  for (const metric of [
    ...metricsForStorage(department.metrics, "month", configByMetric),
    ...metricsNeedingExplicitMonth(department.metrics, configByMetric),
  ]) {
    if (seen.has(metric.id)) continue;
    seen.add(metric.id);
    result.push(metric);
  }

  return result;
}

export function buildTargetEditorSheets(
  department: DepartmentDefinition,
  teams: TeamGroup[],
  targets: TargetRow[],
  anchorDate: Date,
  weekStart: string,
  quarterStart: string,
  configByMetric: Map<string, TargetMetricConfig>,
  monthStarts?: string[],
): TargetEditorSheets {
  const weeklyMetrics = metricsForStorage(department.metrics, "week_default", configByMetric);
  const monthlyMetrics = mergeMonthlyMetrics(department, configByMetric);
  const quarterlyMetrics = metricsNeedingExplicitQuarter(department.metrics, configByMetric);

  const weekly =
    weeklyMetrics.length > 0
      ? {
          recurring: buildPeriodTargetSheetValues(
            department,
            teams,
            targets,
            RECURRING_WEEK_PERIOD_TYPE,
            RECURRING_WEEK_PERIOD_START,
            weeklyMetrics,
          ),
          overrides: buildPeriodTargetSheetValues(
            department,
            teams,
            targets,
            "week",
            weekStart,
            weeklyMetrics,
          ),
        }
      : null;

  const monthlyMonthStarts = monthStarts ?? allMonthStartsInCalendarQuarter(anchorDate);

  const monthlyQuarters =
    monthlyMetrics.length > 0
      ? monthlyMonthStarts.map((monthStartDate) => ({
          monthStart: monthStartDate,
          sheet: buildPeriodTargetSheetValues(
            department,
            teams,
            targets,
            "month",
            monthStartDate,
            monthlyMetrics,
          ),
        }))
      : [];

  const quarterly =
    quarterlyMetrics.length > 0
      ? buildPeriodTargetSheetValues(
          department,
          teams,
          targets,
          "quarter",
          quarterStart,
          quarterlyMetrics,
        )
      : null;

  return { weekly, monthlyQuarters, quarterly };
}

export function isWeekTargetSheet(
  sheet: TargetSheetSlice | WeekTargetSheetValues,
): sheet is WeekTargetSheetValues {
  return "recurring" in sheet;
}
